import { Injectable } from '@angular/core';

export interface MenuItemPermission {
  routerLink?: string;
  permissions?: string[];
  roles?: string[];
  requireAll?: boolean;
  visible?: boolean;
}

export interface ActionPermission {
  action: string;
  permissions?: string[];
  roles?: string[];
  requireAll?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionConfigService {

  // Menu items and their required permissions
  private menuPermissions: { [key: string]: MenuItemPermission } = {
    // Dashboard
    '/admin/dashboard': {
      permissions: ['dashboard.admin.view'],
      roles: ['SA', 'A']
    },
    '/admin/admin-dashboard': {
      permissions: ['dashboard.admin.view'],
      roles: ['SA', 'A']
    },
    '/admin/fsr-dashboard': {
      permissions: [],
      roles: ['FSR']
    },

    // User Management
    '/admin/users': {
      permissions: ['user.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/user-management': {
      permissions: ['user.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/add-edit-user': {
      permissions: ['user.create', 'user.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Customer Management
    '/admin/customers': {
      permissions: ['customer.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    '/admin/add-edit-customer': {
      permissions: ['customer.create', 'customer.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    '/admin/customer-lookup': {
      permissions: ['customer.lookup'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },

    // Reservation Management
    '/admin/reservations': {
      permissions: ['reservation.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    '/admin/add-reservation': {
      permissions: ['reservation.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },

    // Contract Management
    '/admin/contracts': {
      permissions: ['contract.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/add-edit-contract': {
      permissions: ['contract.create', 'contract.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Product Management
    '/admin/product-management': {
      permissions: ['product.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/add-edit-product': {
      permissions: ['product.create', 'product.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Territory Management
    '/admin/territory-management': {
      permissions: ['territory.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    '/admin/territory-management/add': {
      permissions: ['territory.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Role Management
    '/admin/role-management': {
      permissions: ['role.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/base-role-management': {
      permissions: [],
      roles: ['SUPER_DEV_ADMIN']
    },

    // Contract Management
    '/admin/contract-management': {
      permissions: ['contract.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // System Settings
    '/admin/company-configuration': {
      permissions: ['subscriber.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    '/admin/subscriber-management': {
      permissions: ['subscriber.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN']
    },

    // Reports
    '/admin/system-logs': {
      permissions: ['systemlog.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Calendar
    '/admin/calendar': {
      permissions: ['calendar.event.read'],
      roles: ['FSR']
    },

    // Interactions
    '/admin/log-interaction': {
      permissions: ['interaction.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    '/admin/schedule-interaction': {
      permissions: ['interaction.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },

    // Payment/Subscription
    '/admin/payment/portal': {
      permissions: ['billing.subscription.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Message Templates
    '/admin/message-template': {
      permissions: ['messagetemplate.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },

    // Online Handbook
    '/admin/online-handbook': {
      permissions: ['handbook.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    }
  };

  // Action permissions for buttons and operations
  private actionPermissions: { [key: string]: ActionPermission } = {
    'CREATE_USER': {
      action: 'CREATE_USER',
      permissions: ['user.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_USER': {
      action: 'EDIT_USER',
      permissions: ['user.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'DELETE_USER': {
      action: 'DELETE_USER',
      permissions: ['user.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'CREATE_CUSTOMER': {
      action: 'CREATE_CUSTOMER',
      permissions: ['customer.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'EDIT_CUSTOMER': {
      action: 'EDIT_CUSTOMER',
      permissions: ['customer.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'DELETE_CUSTOMER': {
      action: 'DELETE_CUSTOMER',
      permissions: ['customer.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'CREATE_RESERVATION': {
      action: 'CREATE_RESERVATION',
      permissions: ['reservation.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'EDIT_RESERVATION': {
      action: 'EDIT_RESERVATION',
      permissions: ['reservation.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'DELETE_RESERVATION': {
      action: 'DELETE_RESERVATION',
      permissions: ['reservation.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'CREATE_CONTRACT': {
      action: 'CREATE_CONTRACT',
      permissions: ['contract.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_CONTRACT': {
      action: 'EDIT_CONTRACT',
      permissions: ['contract.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'TERMINATE_CONTRACT': {
      action: 'TERMINATE_CONTRACT',
      permissions: ['contract.terminate'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'MANAGE_PRODUCTS': {
      action: 'MANAGE_PRODUCTS',
      permissions: ['product.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'CREATE_PRODUCT': {
      action: 'CREATE_PRODUCT',
      permissions: ['product.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_PRODUCT': {
      action: 'EDIT_PRODUCT',
      permissions: ['product.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'DELETE_PRODUCT': {
      action: 'DELETE_PRODUCT',
      permissions: ['product.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'MANAGE_TERRITORIES': {
      action: 'MANAGE_TERRITORIES',
      permissions: ['territory.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'CREATE_TERRITORY': {
      action: 'CREATE_TERRITORY',
      permissions: ['territory.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_TERRITORY': {
      action: 'EDIT_TERRITORY',
      permissions: ['territory.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'MANAGE_ROLES': {
      action: 'MANAGE_ROLES',
      permissions: ['role.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'MANAGE_SUBSCRIBERS': {
      action: 'MANAGE_SUBSCRIBERS',
      permissions: ['subscriber.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN']
    },
    'VIEW_SYSTEM_LOGS': {
      action: 'VIEW_SYSTEM_LOGS',
      permissions: ['user.read'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'SCHEDULE_INTERACTION': {
      action: 'SCHEDULE_INTERACTION',
      permissions: ['interaction.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    'CREATE_INTERACTION': {
      action: 'CREATE_INTERACTION',
      permissions: ['interaction.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A', 'FSR']
    },
    // FAQ Management
    'CREATE_FAQ': {
      action: 'CREATE_FAQ',
      permissions: ['faq.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_FAQ': {
      action: 'EDIT_FAQ',
      permissions: ['faq.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'DELETE_FAQ': {
      action: 'DELETE_FAQ',
      permissions: ['faq.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    // Video Guide Management
    'CREATE_VIDEO_GUIDE': {
      action: 'CREATE_VIDEO_GUIDE',
      permissions: ['videoguide.create'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'EDIT_VIDEO_GUIDE': {
      action: 'EDIT_VIDEO_GUIDE',
      permissions: ['videoguide.update'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    },
    'DELETE_VIDEO_GUIDE': {
      action: 'DELETE_VIDEO_GUIDE',
      permissions: ['videoguide.delete'],
      roles: ['CSM', 'SM', 'SUPER_DEV_ADMIN', 'SA', 'A']
    }
  };

  /**
   * Get permission requirements for a menu item
   */
  getMenuPermissions(routerLink: string): MenuItemPermission | undefined {
    return this.menuPermissions[routerLink];
  }

  /**
   * Get permission requirements for an action
   */
  getActionPermissions(action: string): ActionPermission | undefined {
    return this.actionPermissions[action];
  }

  /**
   * Get all menu permissions
   */
  getAllMenuPermissions(): { [key: string]: MenuItemPermission } {
    return this.menuPermissions;
  }

  /**
   * Get all action permissions
   */
  getAllActionPermissions(): { [key: string]: ActionPermission } {
    return this.actionPermissions;
  }

  /**
   * Add or update menu permission configuration
   */
  setMenuPermission(routerLink: string, config: MenuItemPermission): void {
    this.menuPermissions[routerLink] = config;
  }

  /**
   * Add or update action permission configuration
   */
  setActionPermission(action: string, config: ActionPermission): void {
    this.actionPermissions[action] = config;
  }
}
